import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, X, Edit2 } from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';
import { API_URL } from '../../config';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { token } = useAuth();

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  const fetchServices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (err) {
      console.error('Hizmetler yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleOpenModal = (service = null) => {
    if (service) {
      setIsEditing(true);
      setSelectedService(service);
      setFormData({
        title: service.title,
        description: service.description
      });
    } else {
      setIsEditing(false);
      setSelectedService(null);
      setFormData({
        title: '',
        description: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = isEditing 
      ? `${API_URL}/admin/services/${selectedService.id}`
      : `${API_URL}/admin/services`;
    
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        Swal.fire({
          icon: 'success',
          title: isEditing ? 'Güncellendi!' : 'Eklendi!',
          text: 'Hizmet bilgileri başarıyla kaydedildi.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff',
          timer: 2000
        });
        fetchServices();
      } else {
        const errData = await response.json();
        Swal.fire({ icon: 'error', title: 'Hata!', text: errData.message || 'İşlem başarısız.', background: '#0f172a', color: '#fff' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Hata!', text: 'Sunucu bağlantı hatası.', background: '#0f172a', color: '#fff' });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu hizmeti silmek istediğinize emin misiniz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      cancelButtonText: 'İptal',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/services/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          Swal.fire({ icon: 'success', title: 'Silindi!', text: 'Hizmet başarıyla silindi.', background: '#0f172a', color: '#fff', timer: 1500 });
          fetchServices();
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Hata!', text: 'İşlem başarısız.', background: '#0f172a', color: '#fff' });
      }
    }
  };

  if (loading) return <div className="admin-card">Hizmetler yükleniyor...</div>;

  return (
    <div className="admin-services">
      <div className="admin-header-flex" style={{ display: 'flex', flexDirection: window.innerWidth < 576 ? 'column' : 'row', justifyContent: 'space-between', alignItems: window.innerWidth < 576 ? 'flex-start' : 'center', marginBottom: '2rem', gap: '1.5rem' }}>
        <div>
          <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.25rem' }}>Hizmet Yönetimi</h1>
          <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>Web sitesindeki hizmetleri buradan yönetebilirsiniz.</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => handleOpenModal()}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.85rem 1.75rem', borderRadius: '1rem', fontWeight: '800', fontSize: '0.9rem', width: window.innerWidth < 576 ? '100%' : 'auto', justifyContent: 'center' }}
        >
          <Plus size={18} /> Yeni Hizmet Ekle
        </button>
      </div>

      <div className="admin-card" style={{ borderRadius: '1.25rem', border: '1px solid var(--navbar-border)', padding: 0, overflow: 'hidden' }}>
        <div className="admin-table-wrapper">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
              <tr>
                <th style={{ padding: '1rem 1.5rem', width: '80px', whiteSpace: 'nowrap' }}>No</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Başlık</th>
                <th style={{ padding: '1rem 1.5rem', whiteSpace: 'nowrap' }}>Açıklama</th>
                <th style={{ padding: '1rem 1.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service, index) => (
                <tr key={service.id} style={{ borderBottom: '1px solid var(--navbar-border)' }}>
                  <td style={{ padding: '1rem 1.5rem', fontWeight: '700', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                    {String(index + 1).padStart(2, '0')}
                  </td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>{service.title}</div>
                  </td>
                  <td style={{ padding: '1rem 1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', minWidth: '300px', maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {service.description}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleOpenModal(service)} style={{ background: 'rgba(255,255,255,0.03)', border: 'none', color: 'white', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Düzenle">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(service.id)} style={{ background: 'rgba(239, 68, 68, 0.05)', border: 'none', color: '#f87171', padding: '0.4rem', borderRadius: '0.5rem', cursor: 'pointer' }} title="Sil">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1.5rem' }}>
          <div className="admin-card" style={{ width: '100%', maxWidth: '600px', background: 'rgba(15, 15, 15, 0.95)', border: '1px solid rgba(0, 209, 255, 0.2)', borderRadius: '2rem', padding: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>{isEditing ? 'HİZMETİ DÜZENLE' : 'YENİ HİZMET EKLE'}</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', marginBottom: '0.5rem', fontSize: '0.85rem' }}>Hizmet Başlığı</label>
                <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '1rem', color: 'white' }} />
              </div>

              <div>
                <label style={{ display: 'block', color: '#64748b', marginBottom: '0.5rem', fontSize: '0.85rem' }}>Açıklama</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="4" required style={{ width: '100%', background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.1)', padding: '1rem', borderRadius: '1rem', color: 'white', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: '1rem', borderRadius: '1rem', background: 'rgba(255, 255, 255, 0.05)', color: 'white', border: 'none', fontWeight: '600' }}>İptal</button>
                <button type="submit" style={{ flex: 2, padding: '1rem', borderRadius: '1rem', background: 'linear-gradient(to right, #00d1ff, #0072ff)', color: 'white', border: 'none', fontWeight: '700' }}>{isEditing ? 'Güncelle' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
